import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Types
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import useQuestionEnum from '../../../../hooks/useQuestionEnum';

import { SearchSelect } from '@wegroup/design-system';
import { isEmpty } from 'lodash';
import { getFlowsDataTestId } from '../../../../utils/testUtils';
import { Inputs } from '../../../../types/Enums';
import useIsInsideModal from '../table/context/useIsInsideModal';

interface Props {
  question: FlowsQuestion;
}

const SelectInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  question,
}) => {
  const { t } = useTranslation();
  const {
    field: { onBlur, value },
    prefilled,
    error,
  } = useFlowInputComponent({ question });
  const isInsideModal = useIsInsideModal();

  const handleOnChange = (value: unknown) => {
    if (typeof value === 'object' && isEmpty(value))
      onBlur({ name: question.id, value: '' });
    else onBlur({ name: question.id, value: value as string });
  };

  const options = useQuestionEnum(question);

  /** When the options change due to conditions, it should double check if the filled in value is still valid
   * If not -> clear the question answer
   */
  useEffect(() => {
    if (
      options.length &&
      value &&
      !options.map(({ value }) => value).includes(value)
    ) {
      handleOnChange(null);
    }
  }, [options.map(({ value }) => value).join()]);

  return (
    <SearchSelect
      clearable
      placeholder={t('DEFAULT_SELECT_PLACEHOLDER')}
      onChange={handleOnChange}
      data={options}
      error={error?.message}
      value={value}
      prefilled={prefilled}
      // Legacy styling to match flows inputs
      extraStyles={{
        input: {
          height: '45px',
          boxShadow:
            '0 4px 8px rgba(0,0,0,0.04),0 0 2px rgba(0,0,0,0.06), 0 0 1px rgba(0,0,0,0.04)',
          borderColor: prefilled ? '#FFBC0F' : 'rgb(228, 228, 228)',
        },
        dropdown: {
          borderColor: 'rgb(228, 228, 228)',
        },
      }}
      data-test-id={getFlowsDataTestId(
        Inputs.SELECT,
        question,
        'SEARCH-SELECT',
      )}
      isDisabled={question.rules.is_disabled}
      comboboxProps={{ withinPortal: isInsideModal }}
    />
  );
};

export default SelectInputComponent;
